"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import {
  AsYouType,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js"
import { ChevronDown, Search } from "lucide-react"

import {
  DEFAULT_PHONE_COUNTRY,
  getPhoneCountries,
  parsePhone,
  type PhoneCountry,
} from "@/lib/phone"
import { cn } from "@/lib/utils"

interface PhoneFieldProps {
  /** Valor em E.164 (`+5511999998888`) ou vazio. Aceita legado BR cru. */
  value: string
  onChange: (e164: string, meta: { isValid: boolean }) => void
  defaultCountry?: CountryCode
  placeholder?: string
  disabled?: boolean
  error?: boolean
  id?: string
  name?: string
  /** Paleta do chrome. `sales` alinha com o form de checkout (tema venda). */
  variant?: "default" | "sales"
  className?: string
  style?: React.CSSProperties
}

const VARIANT_TOKENS = {
  default: {
    border: "border-background-200",
    hover: "hover:border-background-300",
    divider: "border-background-200",
    triggerHover: "hover:bg-background-50",
    code: "text-text-700",
    chevron: "text-text-400",
    input: "text-text-950 placeholder:text-text-400",
  },
  sales: {
    border: "border-sales-line-strong",
    hover: "hover:border-sales-line-strong",
    divider: "border-sales-line-strong",
    triggerHover: "hover:bg-neutral-50",
    code: "text-sales-ink",
    chevron: "text-sales-text-mid",
    input: "text-sales-ink placeholder:text-sales-text-mid",
  },
} as const

function buildE164(country: CountryCode, national: string): string {
  const digits = national.replace(/\D/g, "")
  if (!digits) return ""
  const parsed = parsePhoneNumberFromString(digits, country)
  if (parsed?.isValid()) return parsed.number
  return `+${getCountryCallingCode(country)}${digits}`
}

export function PhoneField({
  value,
  onChange,
  defaultCountry = DEFAULT_PHONE_COUNTRY,
  placeholder = "Número de telefone",
  disabled = false,
  error = false,
  id,
  name,
  variant = "default",
  className,
  style,
}: PhoneFieldProps) {
  const t = VARIANT_TOKENS[variant]
  const countries = useMemo(() => getPhoneCountries(), [])
  const initial = useMemo(
    () => parsePhone(value, defaultCountry),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const [country, setCountry] = useState<CountryCode>(initial.country)
  const [display, setDisplay] = useState(() =>
    initial.national
      ? new AsYouType(initial.country).input(initial.national)
      : ""
  )
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const lastEmitted = useRef(value)

  // Sincroniza quando o valor externo muda (prefill/reset), sem interromper digitação.
  useEffect(() => {
    if (value === lastEmitted.current) return
    const p = parsePhone(value, defaultCountry)
    setCountry(p.country)
    setDisplay(p.national ? new AsYouType(p.country).input(p.national) : "")
    lastEmitted.current = value
  }, [value, defaultCountry])

  function emit(nextCountry: CountryCode, nextDisplay: string) {
    const e164 = buildE164(nextCountry, nextDisplay)
    const parsed = parsePhoneNumberFromString(
      nextDisplay.replace(/\D/g, ""),
      nextCountry
    )
    lastEmitted.current = e164
    onChange(e164, { isValid: parsed?.isValid() ?? false })
  }

  function handleInput(raw: string) {
    const formatted = new AsYouType(country).input(raw)
    setDisplay(formatted)
    emit(country, formatted)
  }

  function handleSelectCountry(next: PhoneCountry) {
    setCountry(next.iso2)
    setOpen(false)
    setQuery("")
    // Reformata o número atual para a máscara do novo país.
    const digits = display.replace(/\D/g, "")
    const formatted = digits ? new AsYouType(next.iso2).input(digits) : ""
    setDisplay(formatted)
    emit(next.iso2, formatted)
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return countries
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.callingCode.includes(q.replace(/\D/g, ""))
    )
  }, [countries, query])

  const currentCode = getCountryCallingCode(country)
  const currentEmoji = countries.find((c) => c.iso2 === country)?.emoji ?? ""

  return (
    <div
      className={cn(
        "text-body flex w-full items-stretch overflow-hidden rounded-xl border bg-white transition-all",
        t.border,
        "focus-within:border-primary-600 focus-within:ring-[3px] focus-within:ring-primary-500/50",
        t.hover,
        disabled && "cursor-not-allowed opacity-50",
        error && "border-error focus-within:ring-error",
        className
      )}
      style={style}
    >
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex shrink-0 items-center gap-1.5 border-r px-3 py-3",
              t.divider,
              t.triggerHover,
              "focus:outline-none disabled:cursor-not-allowed"
            )}
            aria-label="Selecionar país"
          >
            <span className="text-lg leading-none">{currentEmoji}</span>
            <span className={cn("text-sm", t.code)}>+{currentCode}</span>
            <ChevronDown className={cn("h-4 w-4", t.chevron)} />
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={4}
            className={cn(
              "z-50 w-[300px] overflow-hidden rounded-xl border border-background-100 bg-white shadow-lg",
              "animate-in fade-in-0 zoom-in-95"
            )}
          >
            <div className="flex items-center gap-2 border-b border-background-100 px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-text-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar país ou DDI"
                className="w-full bg-transparent text-sm text-text-950 placeholder:text-text-400 focus:outline-none"
              />
            </div>
            <div className="max-h-[240px] overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-text-400">
                  Nenhum país encontrado
                </p>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.iso2}
                    type="button"
                    onClick={() => handleSelectCountry(c)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm",
                      "hover:bg-primary-50 focus:bg-primary-50 focus:outline-none",
                      c.iso2 === country && "bg-primary-50 text-primary-600"
                    )}
                  >
                    <span className="text-lg leading-none">{c.emoji}</span>
                    <span className="min-w-0 flex-1 truncate text-text-950">
                      {c.name}
                    </span>
                    <span className="shrink-0 text-text-400">
                      +{c.callingCode}
                    </span>
                  </button>
                ))
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      <input
        id={id}
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        disabled={disabled}
        value={display}
        onChange={(e) => handleInput(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full bg-transparent px-3.5 py-3 focus:outline-none disabled:cursor-not-allowed",
          t.input
        )}
      />
    </div>
  )
}
